import { useState, useMemo, useEffect } from "react";
import { useTheme } from "@emotion/react";
import { Box, Typography, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
	PieChart,
	ResponsiveContainer,
	Pie,
	Cell,
	Tooltip,
	Sector,
} from "recharts";
import { useTranslation } from "react-i18next";
import ChartPieTooltip from "./ChartPieTooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ColorDisplayAndPicker from "../../../components/common/ColorDisplayAndPicker";

export default function CategoriesPieChart({
	clickedItem,
	setClickedItem,
	transactionsData,
	categoriesData,
	clickedPeriodName,
	selectedGroup,
	setSelectedGroup,
}) {
	const theme = useTheme();
	const { t } = useTranslation();

	const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
	const [activeIndex, setActiveIndex] = useState(null);

	const handleMouseMove = (data, index, e) => {
		if (!e) return;
		setTooltipPos({ x: e.clientX, y: e.clientY });
	};
	const onPieEnter = (_, index) => {
		setActiveIndex(index);
	};

	const handleClick = (entry) => {
		if (clickedItem.type === null) {
			if (!entry || !entry.id) return;
			setClickedItem({
				...clickedItem,
				type: entry.id,
			});
			setSelectedGroup(null);
		} else {
			if (!selectedGroup) {
				const cat = categoriesData.find((c) => c._id === entry.id);
				if (cat) setSelectedGroup(cat);
			}
		}
	};

	const pieData = useMemo(() => {
		if (!transactionsData) return [];

		if (clickedItem.type === null) {
			const income = transactionsData
				.filter((t) => t.type === "income")
				.reduce(
					(sum, t) =>
						sum + Math.abs(t.amount * (t.exchangeRate || 1)),
					0
				);
			const expense = transactionsData
				.filter((t) => t.type === "expense")
				.reduce(
					(sum, t) =>
						sum + Math.abs(t.amount * (t.exchangeRate || 1)),
					0
				);

			const total = income + expense;

			return [
				{
					id: "income",
					name: t("income"),
					value: income,
					percent: total ? ((income / total) * 100).toFixed(2) : 0,
					color: theme.palette.values.positive,
				},
				{
					id: "expense",
					name: t("expense"),
					value: expense,
					percent: total ? ((expense / total) * 100).toFixed(2) : 0,
					color: theme.palette.values.negative,
				},
			];
		}
		const grouped = {};
		transactionsData.forEach((t) => {
			const key = selectedGroup
				? t.subcategoryId || "uncategorized"
				: t.categoryId || "uncategorized";
			if (!grouped[key]) grouped[key] = 0;
			grouped[key] += Math.abs(
				t.exchangeRate !== 1 ? t.amount * t.exchangeRate : t.amount
			);
		});

		const total = Object.values(grouped).reduce((a, b) => a + b, 0);

		if (selectedGroup) {
			const category = selectedGroup;
			const subcategories = (category.subcategories || [])
				.map((sub) => ({ ...sub, value: grouped[sub._id] || 0 }))
				.filter((s) => s.value > 0)
				.sort((a, b) => b.value - a.value);

			const noSubValue = grouped["uncategorized"] || 0;
			if (noSubValue > 0) {
				subcategories.push({
					id: "uncategorized",
					name: t("unSubcategorized"),
					value: noSubValue,
				});
			}

			const n = subcategories.length;
			const baseColor =
				category.color && category.color !== ""
					? category.color
					: theme.palette.categories.default;

			return subcategories.map((sub, index) => ({
				id: sub._id || sub.id,
				name:
					sub.name || t(sub.translationKey) || t("unSubcategorized"),
				value: sub.value,
				percent: total ? ((sub.value / total) * 100).toFixed(2) : 0,
				color: alpha(baseColor, 1 - (index / n) * 0.5),
			}));
		} else {
			return Object.entries(grouped)
				.map(([id, value]) => {
					const cat = categoriesData.find((c) => c._id === id);
					return {
						id,
						name:
							cat?.groupName ||
							t(cat?.groupTranslationKey) ||
							t("uncategorized"),
						value,
						percent: total ? ((value / total) * 100).toFixed(2) : 0,
						color:
							cat?.color && cat?.color !== ""
								? cat.color
								: theme.palette.categories.default,
					};
				})
				.sort((a, b) => b.value - a.value);
		}
	}, [
		transactionsData,
		selectedGroup,
		categoriesData,
		theme,
		t,
		clickedItem.type,
	]);

	useEffect(() => {
		setActiveIndex(null);
		setTooltipPos({ x: 0, y: 0 });
	}, [selectedGroup, pieData]);

	const outerBoxStyles = {
		backgroundColor: theme.palette.background.paper,
		borderRadius: "10px",
		padding: "16px",
		width: 340,
		maxWidth: "100%",
	};

	const piedataBoxStyles = {
		display: "flex",
		flexWrap: "wrap",
		flexDirection: "column",
		alignItems: "flex-start",
		mt: 2,
	};

	if (!clickedItem || !transactionsData || !categoriesData) return null;

	if (
		clickedItem.type === null &&
		pieData.every((entry) => entry.value === 0)
	) {
		return (
			<Box sx={{ ...outerBoxStyles, textAlign: "left", p: 2 }}>
				<Typography
					variant="subtitle1"
					sx={{ mb: 0, textAlign: "center" }}
				>
					{clickedPeriodName}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{t("noIncomeOrExpenseForThisPeriod")}
				</Typography>
			</Box>
		);
	}
	return (
		<Box sx={outerBoxStyles}>
			{selectedGroup && (
				<>
					<Box sx={{ mb: 1, textAlign: "center" }}>
						<Button
							color="primary"
							variant="outlined"
							sx={{ py: 0.5, px: 1 }}
							startIcon={<ArrowBackIcon />}
							onClick={() => setSelectedGroup(null)}
						>
							{t("backToCategoryGroups")}
						</Button>
					</Box>
					<Typography
						variant="subtitle1"
						sx={{ mb: 0, textAlign: "center" }}
					>
						{clickedItem.type === "income"
							? t("incomeInGroup")
							: t("expenseInGroup")}{" "}
						{selectedGroup.groupName ||
							t(selectedGroup.groupTranslationKey)}
					</Typography>
				</>
			)}
			{!selectedGroup && clickedItem.type && (
				<>
					<Box sx={{ mb: 1, textAlign: "center" }}>
						<Button
							color="primary"
							variant="outlined"
							sx={{ py: 0.5, px: 1 }}
							startIcon={<ArrowBackIcon />}
							onClick={() =>
								setClickedItem({ ...clickedItem, type: null })
							}
						>
							{t("backToMonthView")}
						</Button>
					</Box>
					<Typography
						variant="subtitle1"
						sx={{ mb: 0, textAlign: "center" }}
					>
						{clickedItem.type === "income"
							? t("incomeByCategoryGroups")
							: t("expenseByCategoryGroups")}
					</Typography>
				</>
			)}
			<Typography variant="subtitle1" sx={{ mb: 0, textAlign: "center" }}>
				{clickedPeriodName}
			</Typography>
			<Box
				sx={{
					width: 300,
					height: 260,
				}}
			>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={pieData}
							dataKey="value"
							nameKey="name"
							cx={150}
							cy="50%"
							outerRadius={120}
							innerRadius={80}
							label={false}
							onMouseMove={handleMouseMove}
							stroke="none"
							style={{ cursor: "pointer", outline: "none" }}
							activeIndex={activeIndex}
							activeShape={(props) => (
								<Sector
									{...props}
									outerRadius={props.outerRadius + 5}
									innerRadius={props.innerRadius - 5}
								/>
							)}
							onMouseEnter={onPieEnter}
							onMouseLeave={() => setActiveIndex(null)}
							onClick={handleClick}
						>
							{pieData.map((entry) => (
								<Cell key={entry.id} fill={entry.color} />
							))}
						</Pie>
						<Tooltip
							content={
								<ChartPieTooltip tooltipPos={tooltipPos} />
							}
						/>
					</PieChart>
				</ResponsiveContainer>
			</Box>
			<Box sx={piedataBoxStyles}>
				{pieData.map((entry) => (
					<Box
						key={entry.id}
						sx={{
							display: "flex",
							alignItems: "center",
							mr: 2,
							mb: 1,
						}}
					>
						<ColorDisplayAndPicker
							color={entry.color}
							isEditable={false}
						/>
						<Typography
							variant="body2"
							sx={{
								lineHeight: 1,
								display: "flex",
								alignItems: "center",
							}}
						>
							{entry.name}: {entry.percent}%
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);
}
